// ActivityPanel — persistent current-activity panel, CONSTANT size.
// States: idle / task / travel / fight (+ eating bar). Holds the game's ONE
// Stop button. While `fight` is live: compact combat summary (front-of-line
// combatant per side, hp bars first, Flee mirror, foe name clicks through).
// Progress bars pace to be full 0.1s before ends_at using server time.

import { clear, el, flashClass, kbar, tooltip } from '../kit';
import { sendBtn, serverNow } from '../net';
import * as icons from '../icons';
import * as ui from '../ui';
import * as vsj from '../vsj';
import * as sfx from '../audio/sfx';
import { onFxType } from '../fx';

const LEAD_MS = 100;

function pacedFrac(startMs: number, endsMs: number, nowMs: number): number {
  const target = endsMs - LEAD_MS;
  if (target <= startMs) return 1;
  return Math.max(0, Math.min(1, (nowMs - startMs) / (target - startMs)));
}

export function activityPanel(): { root: HTMLElement; refresh(changed: Set<string>): void } {
  const iconHolder = el('div', { style: 'width:50px;height:50px;flex:none;align-self:center' });
  const nameLabel = el('div', {
    style: 'font-size:var(--font-large);overflow:hidden;text-overflow:ellipsis;white-space:nowrap',
  });
  const bar = kbar('', 14);
  const subLabel = el('div', { class: 'k-small k-dim' });
  // the skill this task pays xp into: name + level + progress to next level
  const skillLabel = el('div', { class: 'k-small', style: 'color:var(--c-skill)' });
  const skillBar = kbar('xp', 6);
  const handLabel = el('div', { class: 'k-small k-dim' });
  const handBar = kbar('', 8);
  const eatLabel = el('div', { class: 'k-small k-dim' });
  const eatBar = kbar('ok', 8);

  // fight summary
  const youHp = el('span', { class: 'k-small k-dim' });
  const youBar = kbar('hp-me cmb', 8, true); // stoneheim hp-me green, heals ease up
  const foeName = el('span', {
    class: 'k-small c-creature',
    style: 'flex:1;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;cursor:pointer',
  });
  const foeHp = el('span', { class: 'k-small k-dim' });
  const foeBar = kbar('hp-enemy cmb', 8); // stoneheim hp-enemy red; spawns snap
  const fightBox = el(
    'div',
    { style: 'display:none;flex-direction:column;gap:3px' },
    el('div', { style: 'display:flex;gap:6px' }, el('span', { class: 'k-small', style: 'flex:1', text: 'You' }), youHp),
    youBar.root,
    el('div', { style: 'display:flex;gap:6px' }, foeName, foeHp),
    foeBar.root,
  );
  let foeInfo = '';
  foeName.addEventListener('click', (e) => {
    e.stopPropagation(); // don't also trigger the panel's go-to-activity jump
    if (foeInfo) ui.select(foeInfo);
  });

  const stopBtn = el('button', { class: 'k-btn', text: 'Stop' });
  const turnbackBtn = el('button', { class: 'k-btn', text: 'Turn back' });
  const fleeBtn = el('button', { class: 'k-btn', text: 'Flee' });
  stopBtn.onclick = () => {
    sfx.playEvent('ui_tick');
    sendBtn(vsj.at('current_activity.stop'));
  };
  turnbackBtn.onclick = () => {
    sfx.playEvent('ui_tick');
    sendBtn(vsj.at('current_activity.travel.turn_back'));
  };
  // served turn-back prose rides the tooltip (nothing shown when unserved)
  tooltip(turnbackBtn, () => {
    const note = vsj.at('current_activity.travel.turn_back_note');
    return note != null ? String(note) : null;
  });
  fleeBtn.onclick = () => {
    sfx.playEvent('ui_tick');
    sendBtn(vsj.at('fight.player.flee'));
  };

  const mid = el(
    'div',
    { style: 'flex:1;min-width:0;display:flex;flex-direction:column;gap:3px' },
    nameLabel, bar.root, fightBox, subLabel, skillLabel, skillBar.root, handLabel, handBar.root, eatLabel, eatBar.root,
  );
  const btns = el(
    'div',
    { style: 'display:flex;flex-direction:column;gap:6px;justify-content:center' },
    stopBtn, turnbackBtn, fleeBtn,
  );
  const root = el(
    'div',
    { class: 'k-panel activity-panel', style: 'flex-direction:row;gap:10px' },
    iconHolder, mid, btns,
  );

  // clicking the panel takes you to the activity (served tab: location /
  // crafting / map); the buttons inside keep their own jobs
  root.addEventListener('click', (e) => {
    if ((e.target as HTMLElement).closest('button')) return;
    const tab = vsj.at('current_activity.tab');
    if (typeof tab === 'string' && tab) ui.requestView(tab);
  });

  let iconName = '';
  let fightFoeSig = '';
  let taskDone = false;
  let lastHandFrac = 0;
  let eatWaiting: boolean | null = null; // null forces a label refresh
  let summaryFrontCid: number | null = null; // foes[0].cid — hit-fx routing

  const setIcon = (icon: string) => {
    if (icon === iconName) return;
    iconName = icon;
    clear(iconHolder);
    if (icon) iconHolder.append(icons.plated(icon, 40, 4));
  };

  const rebind = () => {
    const ca = vsj.at('current_activity') ?? {};
    const fight = vsj.at('fight');
    // the served state is the ONE verdict (the driver serves 'fight' while a
    // fight slice is live — never re-derived here)
    const state = String(ca.state ?? 'idle');
    root.style.cursor = typeof ca.tab === 'string' && ca.tab ? 'pointer' : '';
    let icon = '';
    handLabel.style.display = handBar.root.style.display = 'none';
    // skill-xp line: shown while a task (or road handwork) trains a skill
    const skillId = state === 'task' ? String(ca.skill ?? '')
      : state === 'travel' ? String(ca.travel?.handwork?.skill ?? '') : '';
    const sInfo = skillId ? vsj.info('skill_' + skillId) : null;
    skillLabel.style.display = skillBar.root.style.display = sInfo ? '' : 'none';
    if (sInfo) {
      skillLabel.textContent = `${sInfo.name ?? skillId} · Lv ${sInfo.level ?? '?'}`;
      const into = Number(sInfo.xp_into_level ?? 0);
      const toNext = Number(sInfo.xp_to_next ?? 0);
      skillBar.set(toNext <= 0 ? 1 : into / Math.max(1, into + toNext));
    }
    if (state !== 'fight') {
      fightFoeSig = '';
      foeInfo = '';
    }
    fightBox.style.display = state === 'fight' ? 'flex' : 'none';
    subLabel.style.display = state === 'fight' ? 'none' : '';
    nameLabel.style.color = state === 'task' ? 'var(--c-activity)' : 'var(--text)';
    switch (state) {
      case 'idle':
        nameLabel.textContent = 'Pick an activity';
        subLabel.textContent = "If you're lost, see the quest bar at the top of the screen.";
        bar.root.style.display = 'none';
        break;
      case 'task':
        nameLabel.textContent = String(ca.name ?? '');
        icon = String(ca.icon ?? '');
        bar.root.style.display = '';
        subLabel.textContent = `${(Number(ca.cycle_ms ?? 0) / 1000).toFixed(1)}s cycle`;
        sfx.setWorkContext(String(ca.name ?? ''), icon);
        break;
      case 'travel': {
        const tr = ca.travel ?? {};
        nameLabel.textContent = `Traveling to ${tr.dest_name ?? tr.dest ?? '?'}`;
        icon = tr.running ? 'ui_run' : 'ui_walk';
        bar.root.style.display = '';
        if (tr.handwork) {
          handLabel.style.display = handBar.root.style.display = '';
          handLabel.textContent = String(tr.handwork.name ?? 'Handwork');
        }
        break;
      }
      case 'fight': {
        // front-of-line combatant per side: foes[0] is the summary's foe
        const foes: any[] = Array.isArray(fight?.foes) ? fight.foes : [];
        const foe = foes[0] ?? {};
        summaryFrontCid = foe.cid != null ? Number(foe.cid) : null;
        const unknown = !!foe.unknown;
        foeInfo = unknown ? '' : String(foe.info ?? '');
        const fi = vsj.info(String(foe.info ?? '')) ?? {};
        const fname = unknown ? '???' : String(foe.name ?? fi.name ?? 'a foe');
        // name/icon are SERVED (current_activity.name = "Scouting..." /
        // "Fighting X +N"; icon withheld while scouting)
        nameLabel.textContent = String(ca.name ?? '');
        // the queued line-mates ride as a served-count suffix
        foeName.textContent = foes.length > 1 ? `${fname} +${foes.length - 1}` : fname;
        if (unknown) {
          foeHp.textContent = '? / ?';
          fightFoeSig = '';
          foeBar.set(0);
        } else {
          const sig = `${foe.info}/${fname}`;
          if (sig !== fightFoeSig) {
            fightFoeSig = sig;
            foeBar.set(Number(foe.hp ?? 0) / Math.max(1, Number(foe.hp_max ?? 1))); // fresh spawn snaps
          } else foeBar.setHp(Number(foe.hp ?? 0), Number(foe.hp_max ?? 1));
          foeHp.textContent = `${foe.hp ?? '?'} / ${foe.hp_max ?? '?'}`;
        }
        const p = fight?.player ?? {};
        youHp.textContent = `${p.hp ?? '?'} / ${p.hp_max ?? '?'}`;
        youBar.setHp(Number(p.hp ?? 0), Number(p.hp_max ?? 1));
        icon = String(ca.icon ?? '');
        bar.root.style.display = 'none';
        break;
      }
    }
    setIcon(icon);
    // btn-null availability IS the visibility rule: the driver serves stop
    // only for tasks, turn_back only on the road, flee only in live fights
    stopBtn.style.display = ca.stop ? '' : 'none';
    stopBtn.disabled = !ca.stop;
    fleeBtn.style.display = fight?.player?.flee ? '' : 'none';
    turnbackBtn.style.display = ca.travel?.turn_back ? '' : 'none';
    const eating = ca.eating;
    eatLabel.style.display = eatBar.root.style.display = eating ? '' : 'none';
    eatWaiting = null; // tick re-picks wait prose vs "Eating <name>"
  };
  rebind();

  // compact bars feel damaging hits too (fx who = attacker → victim flashes;
  // a strike on a queued line-mate — cid off the front — stays quiet here)
  onFxType('hit', (f: any) => {
    if (fightBox.style.display === 'none' || String(f.kind ?? 'hit') !== 'hit') return;
    const mine = String(f.who ?? 'player') === 'player';
    if (mine && f.cid != null && summaryFrontCid != null && Number(f.cid) !== summaryFrontCid) return;
    (mine ? foeBar : youBar).flash();
  });

  const tick = () => {
    const ca = vsj.at('current_activity') ?? {};
    const now = serverNow();
    const state = String(ca.state ?? 'idle');
    if (state === 'task') {
      const frac = pacedFrac(Number(ca.started_at ?? 0), Number(ca.ends_at ?? 0), now);
      if (frac >= 1) {
        if (!taskDone) {
          taskDone = true;
          // cycle complete: the family work sound (seeded pitch/timing walk
          // — the sanctioned prediction), same moment as the flash
          sfx.playWork();
          bar.flash();
          const art = iconHolder.querySelector('.k-icon');
          if (art) flashClass(art, 'work-squash');
        }
        bar.set(0); // rest empty until the next cycle's timestamps arrive
      } else {
        taskDone = false;
        bar.set(frac);
      }
    } else if (state === 'travel') {
      const tr = ca.travel ?? {};
      bar.set(pacedFrac(Number(tr.depart_at ?? 0), Number(tr.arrive_at ?? 0), now));
      const eta = Math.max(0, (Number(tr.arrive_at ?? 0) - now) / 1000);
      subLabel.textContent = `ETA ${Math.ceil(eta)}s${tr.running ? ' (running)' : ''}`;
      if (tr.handwork) {
        const cyc = Number(tr.handwork.cycle_ms ?? 1);
        const hend = Number(tr.handwork.ends_at ?? 0);
        const hfrac = pacedFrac(hend - cyc, hend, now);
        // handwork cycle done: same work voice, named by the task
        if (hfrac >= 1 && lastHandFrac < 1) sfx.playWork(String(tr.handwork.name ?? ''));
        lastHandFrac = hfrac;
        handBar.set(hfrac);
      }
    }
    const eating = ca.eating;
    if (eating) {
      // the bar is the CHEW ONLY; before starts_at (meal gated behind a
      // pending swing) it sits empty under the served wait prose
      const dur = Math.max(1, Number(eating.duration_ms ?? 1));
      const end = Number(eating.ends_at ?? 0);
      const starts = Number(eating.starts_at ?? end - dur);
      const waiting = now < starts && eating.wait_text != null;
      if (waiting !== eatWaiting) {
        eatWaiting = waiting;
        eatLabel.textContent = waiting
          ? String(eating.wait_text)
          : `Eating ${vsj.infoName(String(eating.item ?? ''))}`;
      }
      eatBar.set(now < starts ? 0 : pacedFrac(starts, end, now));
    }
    requestAnimationFrame(tick);
  };
  requestAnimationFrame(tick);

  return {
    root,
    refresh(changed) {
      if (changed.has('current_activity') || changed.has('fight') || changed.has('infos') || changed.has('*'))
        rebind();
    },
  };
}
